// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Supabase
import { supabase } from './supabase/supabaseClient';

// Context Providers
import { AuthProvider } from './store/authContext';
import { FinanceProvider } from './store/financeContext';
import { MarketingProvider } from './store/marketingContext';
import { TeamProvider } from './store/teamContext';
import { ProductProvider } from './store/productContext';

// Layout e Rotas
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Páginas
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Financas from './pages/Financas';
import TrafegoMarketing from './pages/TrafegoMarketing';
import Colaboradores from './pages/Colaboradores';
import Produtos from './pages/Produtos';
import UserManagement from './pages/UserManagement';
// Páginas de perfil e configurações do sistema
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Help from './pages/Help';

// Componentes de Visualização
import { 
  KpiCard, 
  KpiCardGroup, 
  MetricChart, 
  PerformanceBar, 
  DistributionPie, 
  ScatterAnalysis, 
  MetricTable,
  FunnelChart,
  ComparisonChart,
  DataWidget,
  InsightGenerator,
  TrendLine
} from './components/Visualization';

// Utilitários
import { dataUtils } from './utils/dataUtils';

// Outros
import LoadingScreen from './components/UI/LoadingScreen';
import './assets/styles/global.css';

function App() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const { error } = await supabase.from('users').select('id').limit(1);
        if (error && error.code !== 'PGRST116') {
          setInitError('Erro ao conectar ao banco de dados.');
        }
      } catch (err) {
        console.error('Erro de inicialização:', err);
        setInitError('Erro ao inicializar o aplicativo.');
      } finally {
        setIsInitializing(false);
      }
    };
    initializeApp();
  }, []);

  if (isInitializing) return <LoadingScreen message="Inicializando aplicativo..." />;

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 text-center p-4">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Erro de Inicialização</h1>
        <p className="mb-4">{initError}</p>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={() => window.location.reload()}
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <AuthProvider>
      <FinanceProvider>
        <MarketingProvider>
          <TeamProvider>
            <ProductProvider>
              <Router>
                <Routes>
                  {/* Páginas públicas */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />

                  {/* Rotas protegidas */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                      {/* Dashboard - acesso geral */}
                      <Route path="/" element={<Dashboard />} />

                      {/* Finanças */}
                      <Route element={<ProtectedRoute requiredPermission="view_finances" />}>
                        <Route path="/financas" element={<Financas />} />
                      </Route>

                      {/* Tráfego e Marketing */}
                      <Route element={<ProtectedRoute requiredPermission="view_traffic" />}>
                        <Route path="/trafego-marketing" element={<TrafegoMarketing />} />
                      </Route>

                      {/* Colaboradores */}
                      <Route element={<ProtectedRoute requiredPermission="view_team" />}>
                        <Route path="/colaboradores" element={<Colaboradores />} />
                      </Route>

                      {/* Produtos */}
                      <Route element={<ProtectedRoute requiredPermission="view_products" />}>
                        <Route path="/produtos" element={<Produtos />} />
                      </Route>

                      {/* Usuários */}
                      <Route element={<ProtectedRoute requiredPermission="edit_team" />}>
                        <Route path="/usuarios" element={<UserManagement />} />
                      </Route>

                      {/* Páginas de perfil e sistema (acesso geral) */}
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/help" element={<Help />} />
                    </Route>
                  </Route>

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Router>
            </ProductProvider>
          </TeamProvider>
        </MarketingProvider>
      </FinanceProvider>
    </AuthProvider>
  );
}

export default App;
